import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const SessionManager = ({ 
  sessionTimeout = 900000,
  warningTime = 120000,
  onSessionExpired,
  onSessionExtended
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());

  const extendSession = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
    if (onSessionExtended) {
      onSessionExtended();
    }
  }, [onSessionExtended]);

  const handleSessionExpired = useCallback(() => {
    setShowWarning(false);
    setLastActivity(Date.now());
    // No redirect - app is public
  }, []);

  const handleActivity = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events?.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      events?.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);

  useEffect(() => {
    const checkSession = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeUntilExpiry = sessionTimeout - timeSinceActivity;

      if (timeUntilExpiry <= 0) {
        handleSessionExpired();
        clearInterval(checkSession);
      } else if (timeUntilExpiry <= warningTime && !showWarning) {
        setShowWarning(true);
        setTimeRemaining(Math.floor(timeUntilExpiry / 1000));
      } else if (showWarning) {
        setTimeRemaining(Math.floor(timeUntilExpiry / 1000));
      }
    }, 1000);

    return () => clearInterval(checkSession);
  }, [lastActivity, sessionTimeout, warningTime, showWarning, handleSessionExpired]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  if (!showWarning) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-background">
      <div className="bg-card rounded-xl shadow-elevation-5 border border-border p-8 max-w-md w-full mx-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg">
            <Icon name="Clock" size={24} color="var(--color-warning)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Session Expiring</h2>
            <p className="text-sm text-muted-foreground">Your session is about to expire</p>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Time Remaining</span>
            <span className="text-2xl font-bold font-mono text-warning">
              {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="w-full bg-background rounded-full h-2 overflow-hidden">
            <div 
              className="bg-warning h-full transition-all duration-1000 ease-linear"
              style={{ width: `${(timeRemaining / (warningTime / 1000)) * 100}%` }}
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          For your security, you will be automatically logged out due to inactivity. 
          Click "Continue Session" to remain logged in.
        </p>

        <div className="flex gap-3">
          <Button
            variant="outline"
            fullWidth
            onClick={extendSession}
          >
            Dismiss
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={extendSession}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Continue Session
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;